import {
  Direction,
  directionRotate180,
  directionRotateCCW,
  directionRotateCW,
} from '@/utils/direction';
import { Side } from '@/schemas/common';
import { Point } from '@/models/Point';

export enum Rotation {
  CW_0,
  CW_90,
  CW_180,
  CW_270,
}

export const rotateCW = (rotation: Rotation): Rotation => {
  switch (rotation) {
    case Rotation.CW_0:
      return Rotation.CW_90;
    case Rotation.CW_90:
      return Rotation.CW_180;
    case Rotation.CW_180:
      return Rotation.CW_270;
    case Rotation.CW_270:
      return Rotation.CW_0;
  }
};

export const rotateCCW = (rotation: Rotation): Rotation => {
  switch (rotation) {
    case Rotation.CW_0:
      return Rotation.CW_270;
    case Rotation.CW_90:
      return Rotation.CW_0;
    case Rotation.CW_180:
      return Rotation.CW_90;
    case Rotation.CW_270:
      return Rotation.CW_180;
  }
};

export const rotate180 = (rotation: Rotation): Rotation => {
  switch (rotation) {
    case Rotation.CW_0:
      return Rotation.CW_180;
    case Rotation.CW_90:
      return Rotation.CW_270;
    case Rotation.CW_180:
      return Rotation.CW_0;
    case Rotation.CW_270:
      return Rotation.CW_90;
  }
};

export const rotateDirection = (direction: Direction, rotation: Rotation): Direction => {
  switch (rotation) {
    case Rotation.CW_0:
      return direction;
    case Rotation.CW_90:
      return directionRotateCW(direction);
    case Rotation.CW_180:
      return directionRotate180(direction);
    case Rotation.CW_270:
      return directionRotateCCW(direction);
  }
};

const SIDES_CW = [Side.TOP, Side.RIGHT, Side.BOTTOM, Side.LEFT] as const;

/** Compose two rotations (addition mod 4). */
export const composeRotation = (a: Rotation, b: Rotation): Rotation =>
  ((a + b) % 4) as Rotation;

/** Rotate a Side by a Rotation. TOP + CW_90 → RIGHT, etc. */
export const rotateSide = (side: Side, rotation: Rotation): Side => {
  const index = SIDES_CW.indexOf(side);
  return SIDES_CW[(index + rotation) % 4];
};

/** Rotate a grid point around an origin. CW_90: (dx,dy) → (-dy, dx). */
export const rotatePointAroundOrigin = (
  point: Point,
  origin: Point,
  rotation: Rotation,
): Point => {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  switch (rotation) {
    case Rotation.CW_0:
      return new Point(origin.x + dx, origin.y + dy);
    case Rotation.CW_90:
      return new Point(origin.x - dy, origin.y + dx);
    case Rotation.CW_180:
      return new Point(origin.x - dx, origin.y - dy);
    case Rotation.CW_270:
      return new Point(origin.x + dy, origin.y - dx);
  }
};

export const rotationToRadians = (rotation: Rotation): number => {
  switch (rotation) {
    case Rotation.CW_0:
      return 0;
    case Rotation.CW_90:
      return Math.PI / 2;
    case Rotation.CW_180:
      return Math.PI;
    case Rotation.CW_270:
      return (3 * Math.PI) / 2;
  }
};
